import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, notification } from "antd";
import { DateTime } from "luxon";
import { ResumeIcon, PauseIcon } from "../../components";
import ExecutionDeviceSelectionModal from "../testcaseCreation/components/testCaseCreatedResult/modals/ExecutionDeviceSelectionModal";
import ExecutionMonitoringModal from "../testcaseCreation/components/testCaseCreatedResult/modals/ExecutionMonitoringModal";
import { updateMonitorJob, disableMonitor } from "./action";
import {
  notifyInProgress,
  notifySuccess,
  notifyError,
} from "../../utils/notification";
import {
  PAUSE_RESUME_BUTTON,
  SCHEDULE_STATE,
  MONITOR_STATUS,
} from "../../types/common";
import { getFormattedISODateTime } from "../../utils/dateUtils";

const MonitoringScheduleItem = ({ job }: any) => {
  const [buttonValue, setButtonValue] = useState<string>(
    PAUSE_RESUME_BUTTON.PAUSE
  );
  const [isEMModal, setEMModal] = useState<boolean>(false);
  const [isEDSModal, setEDSModal] = useState<boolean>(false);
  const [isButtonActionLoading, setButtonActionLoading] =
    useState<boolean>(false);
  // eslint-disable-next-line no-restricted-globals
  const searchParams = new URLSearchParams(location.search);

  const [form] = Form.useForm();

  const toDateValue = (isoValue?: string) =>
    isoValue ? DateTime.fromISO(isoValue) : null;

  const toDateTime = (value: any) => {
    if (!value) return null;
    if (DateTime.isDateTime(value)) return value as DateTime;
    if (value?.toDate) return DateTime.fromJSDate(value.toDate());
    if (value instanceof Date) return DateTime.fromJSDate(value);
    const parsed = DateTime.fromISO(value as string);
    return parsed.isValid ? parsed : null;
  };

  const handleButtonAction = async () => {
    if (buttonValue === PAUSE_RESUME_BUTTON.PAUSE) {
      setButtonActionLoading(true);
      await disableMonitor({
        accountId: job.accountId,
        monitorId: job.monitorId,
      });
      setButtonValue(PAUSE_RESUME_BUTTON.RESUME);
      setButtonActionLoading(false);
    } else {
      setEMModal(true);
    }
  };

  const updateMonitor = async (selectedEDB: any, region: string) => {
    try {
      setButtonActionLoading(true);
      notifyInProgress("Updating the monitor");
      const fData = form.getFieldsValue();

      const fromDate = toDateTime(fData.fromdate);
      const fromTime = toDateTime(fData.starttime);
      const toDate = toDateTime(fData.todate);
      const toTime = toDateTime(fData.endtime);

      if (!fromDate || !fromTime || !toDate || !toTime) {
        throw new Error("Invalid schedule date/time values");
      }

      const startDate = DateTime.fromObject({
        year: fromDate.year,
        month: fromDate.month,
        day: fromDate.day,
        hour: fromTime.hour,
        minute: fromTime.minute,
        second: fromTime.second,
      });

      const endDate = DateTime.fromObject({
        year: toDate.year,
        month: toDate.month,
        day: toDate.day,
        hour: toTime.hour,
        minute: toTime.minute,
        second: toTime.second,
      });

      const queryParameters = {
        accountId: job.accountId,
        monitorId: job.monitorId,
        status: SCHEDULE_STATE.ENABLED,
      };

      const payload = {
        startDate,
        endDate,
        repeatFrequency: fData.frequency,
        failAlertNotifier: fData.email,
        selectedEDB,
        failedNotifyFailCount: parseInt(fData?.failedNotifyFailCount, 10) || 2,
        selectedRegion: region,
      };

      await updateMonitorJob(queryParameters, payload);
      notification.destroy();
      notifySuccess("Monitor updated successfully!");
      setButtonValue(PAUSE_RESUME_BUTTON.PAUSE);
    } catch (err) {
      notifyError("Failed to resume monitor");
      console.log(err);
    }
    setButtonActionLoading(false);
  };

  useEffect(() => {
    if (!job) return;
    const fData = {
      fromdate: toDateValue(job?.startDate),
      starttime: toDateValue(job?.startDate),
      todate: toDateValue(job?.endDate),
      endtime: toDateValue(job?.endDate),
      frequency: job?.repeatFrequency,
      email: job?.failAlertNotifier,
      failedNotifyFailCount: job?.failedNotifyFailCount || 2,
    };
    form.setFieldsValue(fData);
    if (job.status === MONITOR_STATUS.IN_PROGRESS) {
      setButtonValue(PAUSE_RESUME_BUTTON.PAUSE);
    } else if (job.status === MONITOR_STATUS.PAUSED) {
      setButtonValue(PAUSE_RESUME_BUTTON.RESUME);
    }
  }, [job, form]);

  return (
    <div className="fp_test_exec_list_item">
      <Row justify="start" className="fp_list_item_new">
        <Col span={4} className="right-border">
          <p>{`Every ${job.repeatFrequency}`}</p>
        </Col>
        <Col
          span={10}
          className="fp_list_item_module column-align right-border"
        >
          <p>
            {DateTime.fromISO(job?.startDate).toLocaleString(
              DateTime.DATETIME_MED
            )}
          </p>
          <span className="dash-icon">-</span>
          <p>
            {DateTime.fromISO(job?.endDate).toLocaleString(
              DateTime.DATETIME_MED
            )}
          </p>
        </Col>
        <Col span={5} className="column-align right-border">
          <p>{getFormattedISODateTime(job?.lastRun)}</p>
        </Col>
        {job.status === MONITOR_STATUS.COMPLETE ? (
          <Col span={4} className="column-align">
            <p style={{ color: "rgba(34, 169, 134, 1)" }}>{job.status}</p>
          </Col>
        ) : (
          <Col span={3} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={
                isButtonActionLoading ||
                searchParams.get("isGuestUrl") === "true"
              }
              loading={isButtonActionLoading}
              type="primary"
              className={`fp_btn fp_btn-view ${
                buttonValue === PAUSE_RESUME_BUTTON.PAUSE
                  ? "fp_btn-view-pause_button"
                  : "fp_btn-view-resume_button"
              }`}
              icon={
                buttonValue === PAUSE_RESUME_BUTTON.PAUSE ? (
                  <PauseIcon />
                ) : (
                  <ResumeIcon />
                )
              }
              onClick={handleButtonAction}
            >
              {buttonValue}
            </Button>
          </Col>
        )}
      </Row>
      <ExecutionDeviceSelectionModal
        open={isEDSModal}
        onRun={updateMonitor}
        selectedRegion={job?.selectedRegion}
        onClose={() => {
          setEDSModal(false);
        }}
        buttonName="Monitor"
      />
      <ExecutionMonitoringModal
        open={isEMModal}
        form={form}
        onClose={(flag: boolean) => {
          setEMModal(false);
          if (flag) {
            setEDSModal(true);
          }
        }}
      />
    </div>
  );
};

export default MonitoringScheduleItem;
